// SQLite WASM database layer.
// Initializes sql.js, creates schema, persists to IndexedDB.

import initSqlJs from 'sql.js/dist/sql-wasm-browser.js'
import { TABLES, INDEXES, SCHEMA_VERSION } from './schema'

const DB_NAME = 'geekgroup_offline'
const DB_STORE = 'database'

let _db = null
let _ready = null

// ---- IndexedDB persistence ----

function openIDB() {
	return new Promise(function(resolve, reject) {
		var request = indexedDB.open(DB_NAME, 1)
		request.onupgradeneeded = function() {
			request.result.createObjectStore(DB_STORE)
		}
		request.onsuccess = function() { resolve(request.result) }
		request.onerror = function() { reject(request.error) }
	})
}

function loadFromIDB() {
	return openIDB().then(function(idb) {
		return new Promise(function(resolve, reject) {
			var tx = idb.transaction(DB_STORE, 'readonly')
			var store = tx.objectStore(DB_STORE)
			var request = store.get('db')
			request.onsuccess = function() { resolve(request.result || null) }
			request.onerror = function() { reject(request.error) }
		})
	})
}

function saveToIDB(data) {
	return openIDB().then(function(idb) {
		return new Promise(function(resolve, reject) {
			var tx = idb.transaction(DB_STORE, 'readwrite')
			var store = tx.objectStore(DB_STORE)
			var request = store.put(data, 'db')
			request.onsuccess = function() { resolve() }
			request.onerror = function() { reject(request.error) }
		})
	})
}

// ---- Database lifecycle ----

function createSchema(db) {
	db.run(TABLES)
	db.run(INDEXES)
	db.run('INSERT OR REPLACE INTO _offline_meta (key, value) VALUES (?, ?)', ['schema_version', String(SCHEMA_VERSION)])
}

function checkSchemaVersion(db) {
	try {
		var result = db.exec('SELECT value FROM _offline_meta WHERE key = ?', ['schema_version'])
		if (result.length && result[0].values.length) {
			return parseInt(result[0].values[0][0], 10) === SCHEMA_VERSION
		}
	} catch (e) {
		// table doesn't exist yet
	}
	return false
}

// Optional pre-built SQLite buffer. If set before init(), the DB is
// constructed directly from these bytes (skipping schema creation and
// the IndexedDB load path). Used by Sync.snapshotSqlite() to mount a
// pre-built .snapshot.sqlite from the API in one shot.
var _pendingBuffer = null

function init() {
	if (_ready) return _ready

	// Fast path: if a pre-built buffer was handed to us, mount it
	// directly and skip the IndexedDB lookup. This is the
	// snapshot.sqlite cold-start path — no JSON.parse, no per-row
	// INSERTs, no schema setup.
	if (_pendingBuffer) {
		var buf = _pendingBuffer
		_pendingBuffer = null
		_ready = initSqlJs({
			locateFile: function() { return '/static/sql/sql-wasm.wasm' }
		}).then(function(SQL) {
			_db = new SQL.Database(buf)
			_db.run('PRAGMA journal_mode = MEMORY')
			_db.run('PRAGMA synchronous = OFF')
			_db.run('PRAGMA cache_size = -8000')
			return _db
		})
		return _ready
	}

	_ready = Promise.all([
		initSqlJs({
			locateFile: function() { return '/static/sql/sql-wasm.wasm' }
		}),
		loadFromIDB()
	]).then(function(results) {
		var SQL = results[0]
		var savedData = results[1]

		if (savedData) {
			_db = new SQL.Database(new Uint8Array(savedData))
			if (!checkSchemaVersion(_db)) {
				_db.close()
				_db = new SQL.Database()
				createSchema(_db)
			}
		} else {
			_db = new SQL.Database()
			createSchema(_db)
		}

		// Enable WAL-like performance (not actual WAL in sql.js, but helps)
		_db.run('PRAGMA journal_mode = MEMORY')
		_db.run('PRAGMA synchronous = OFF')
		_db.run('PRAGMA cache_size = -8000')

		return _db
	})

	return _ready
}

// ---- Public API ----

var _profiling = false
var _profile = []
var _phaseStack = []

var DB = {

	// ---- Profiling API ----

	startProfiling: function() {
		_profiling = true
		_profile = []
		_phaseStack = []
	},

	stopProfiling: function() {
		_profiling = false
		return _profile.slice()
	},

	getProfile: function() {
		return _profile.slice()
	},

	clearProfile: function() {
		_profile = []
	},

	isProfiling: function() {
		return _profiling
	},

	// Mark the start of a named phase (template, search, package, data)
	phaseStart: function(name) {
		if (!_profiling) return
		_phaseStack.push({ name: name, start: performance.now() })
	},

	// Mark the end of the current phase and record it
	phaseEnd: function() {
		if (!_profiling || !_phaseStack.length) return
		var phase = _phaseStack.pop()
		_profile.push({
			type: 'phase',
			label: phase.name,
			ms: Math.round((performance.now() - phase.start) * 100) / 100
		})
	},

	// Initialize and return the database. Safe to call multiple times.
	init: init,

	// Get the raw sql.js Database instance (must call init first).
	get: function() {
		return _db
	},

	// Run a query that returns rows. Returns array of objects.
	// e.g. DB.query('SELECT * FROM game WHERE game_id = ?', [123])
	// => [{ game_id: 123, game_name: 'Catan', ... }]
	query: function(sql, params) {
		if (!_db) throw new Error('Database not initialized')
		var start = _profiling ? performance.now() : 0
		var stmt = _db.prepare(sql)
		if (params) stmt.bind(params)

		var rows = []
		while (stmt.step()) {
			rows.push(stmt.getAsObject())
		}
		stmt.free()

		if (_profiling) {
			_profile.push({
				type: 'query',
				sql: sql.length > 300 ? sql.substring(0, 300) + '...' : sql,
				rows: rows.length,
				ms: Math.round((performance.now() - start) * 100) / 100
			})
		}

		return rows
	},

	// Run a query and return the raw sql.js exec result.
	// Returns [{ columns: [...], values: [[...], ...] }]
	exec: function(sql, params) {
		if (!_db) throw new Error('Database not initialized')
		return _db.exec(sql, params)
	},

	// Faster query path for hot loops over thousands of rows. Uses
	// db.exec() (which returns a single columns/values pair) instead of
	// the per-row stmt.getAsObject() in DB.query(). For large result sets
	// (~thousands of rows) this is roughly 2x faster because it avoids
	// repeated property-bag construction inside sql.js. Only use it where
	// the per-row overhead matters; DB.query is fine everywhere else.
	queryFast: function(sql) {
		if (!_db) throw new Error('Database not initialized')
		var result = _db.exec(sql)
		if (!result || !result.length) return []
		var columns = result[0].columns
		var values = result[0].values
		var rows = new Array(values.length)
		var colCount = columns.length
		for (var i = 0; i < values.length; i++) {
			var row = {}
			var v = values[i]
			for (var c = 0; c < colCount; c++) row[columns[c]] = v[c]
			rows[i] = row
		}
		return rows
	},

	// Run a statement that doesn't return rows (INSERT, UPDATE, DELETE, etc.)
	run: function(sql, params) {
		if (!_db) throw new Error('Database not initialized')
		_db.run(sql, params)
	},

	// Persist current database state to IndexedDB.
	save: function() {
		if (!_db) return Promise.resolve()
		var data = _db.export()
		return saveToIDB(data.buffer)
	},

	// Wipe the local database and recreate schema.
	reset: function() {
		if (_db) _db.close()
		_pendingBuffer = null
		return initSqlJs({
			locateFile: function() { return '/static/sql/sql-wasm.wasm' }
		}).then(function(SQL) {
			_db = new SQL.Database()
			createSchema(_db)
			return DB.save()
		})
	},

	// Mount a pre-built SQLite database from a Uint8Array. Used by
	// Sync.snapshotSqlite() to load a server-generated .sqlite snapshot
	// in one shot — no JSON.parse, no per-table importTable. Resets the
	// in-memory DB, replaces it with the buffer contents, and persists
	// to IndexedDB.
	initFromBuffer: function(buf) {
		var bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
		if (_db) {
			_db.close()
			_db = null
		}
		_ready = null
		_pendingBuffer = bytes
		return init().then(function() {
			return DB.save()
		})
	},

	// Bulk import rows into a table. Uses a transaction for performance.
	// rows is an array of objects, keys matching column names.
	importTable: function(table, rows) {
		if (!_db || !rows || !rows.length) return

		var columns = Object.keys(rows[0])
		var placeholders = columns.map(function() { return '?' }).join(',')
		var sql = 'INSERT OR REPLACE INTO ' + table + ' (' + columns.join(',') + ') VALUES (' + placeholders + ')'

		_db.run('BEGIN TRANSACTION')
		try {
			var stmt = _db.prepare(sql)
			for (var i = 0; i < rows.length; i++) {
				var values = columns.map(function(col) {
					var v = rows[i][col]
					// Stringify objects/arrays for JSON columns
					if (v !== null && v !== undefined && typeof v === 'object') return JSON.stringify(v)
					return (v === undefined) ? null : v
				})
				stmt.run(values)
			}
			stmt.free()
			_db.run('COMMIT')
		} catch (e) {
			_db.run('ROLLBACK')
			throw e
		}
	},

	// Clear all data from a table.
	truncate: function(table) {
		if (!_db) return
		_db.run('DELETE FROM ' + table)
	},

	// Get a single scalar value.
	scalar: function(sql, params) {
		if (!_db) throw new Error('Database not initialized')
		var start = _profiling ? performance.now() : 0
		var result = _db.exec(sql, params)
		if (_profiling) {
			_profile.push({
				type: 'scalar',
				sql: sql.length > 300 ? sql.substring(0, 300) + '...' : sql,
				ms: Math.round((performance.now() - start) * 100) / 100
			})
		}
		if (result.length && result[0].values.length) {
			return result[0].values[0][0]
		}
		return null
	},

	// Run EXPLAIN QUERY PLAN on a SQL statement. Returns the plan rows.
	explain: function(sql, params) {
		if (!_db) throw new Error('Database not initialized')
		return _db.exec('EXPLAIN QUERY PLAN ' + sql, params)
	},

	// Check if database has been populated with data.
	hasData: function() {
		if (!_db) return false
		var count = DB.scalar('SELECT COUNT(*) FROM _offline_meta WHERE key = ?', ['last_sync'])
		return count > 0
	},

	// Get/set metadata values.
	meta: function(key, value) {
		if (value === undefined) {
			return DB.scalar('SELECT value FROM _offline_meta WHERE key = ?', [key])
		}
		DB.run('INSERT OR REPLACE INTO _offline_meta (key, value) VALUES (?, ?)', [key, String(value)])
	}

}

export default DB
